import React, {useCallback, useEffect, useState} from "react";
import {Card} from "react-bootstrap";
import {Location, User, Visit, Session, SettingsDoc} from "../../types/Types";
import {collection, doc, getDoc, getDocs, limit, orderBy, query, where} from "@firebase/firestore";
import {db, functions} from "../../../firebase/FirebaseProvider";
import LocationNav from "./LocationNav";
import LocationDetails from "./LocationDetails";
import LocationDetailsV2 from "./LocationDetailsV2";
import {LOCATION_LIMIT, logDataLoaded} from "../../util/Util";
import LocationCheckIn from "./LocationCheckIn";
import UserTag from "../../users/tag/UserTag";
import DateTime from "../../date/DateTime";
import PageTracking from "../../auth/PageTracking";
import {Link, useParams} from 'react-router-dom';
import {httpsCallable} from "@firebase/functions";
import {V2GlassChip, V2GlassPanel, V2GlassSurface} from "../../shared/v2/V2Glass";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";

interface Props {
    currentUser: User | undefined,
    usersMap: Map<string, User> | undefined
    sessionsMap: Map<string, Session>;
    settings: SettingsDoc | undefined;
    likesMap: Map<string, boolean>
}

function LocationVisits(props: Props) {
    const { id } = useParams<{ id: string }>();
    const [location, setLocation] = useState<Location | undefined>(undefined);
    const [visits, setVisits] = useState<Array<Visit> | undefined>(undefined);
    const [visited, setVisited] = useState<boolean>(false);

    const isUserSocial = useCallback((visitId: string): boolean => {
        if(!props.usersMap) {
            return false;
        }
        let user: User | undefined = props.usersMap.get(visitId);
        if(!user) {
            return false;
        }
        return !user.socialDisabled;
    }, [props.usersMap]);

    const loadVisits = useCallback(async() => {
        if(!location) {
            return;
        }
        const visits: Array<Visit> = new Array<Visit>();
        const visitsOnceCollection = query(collection(db, 'visits'),
            where('locationId', '==', location.id),
            where("visited", "==", true),
            orderBy('lastModified', 'desc'),
            limit(LOCATION_LIMIT));
        const visitsCollectionSnapshot = await getDocs(visitsOnceCollection);
        visitsCollectionSnapshot.forEach((doc) => {
            let tempVisit: Visit = doc.data() as Visit;
            if(isUserSocial(tempVisit.userId)) {
                visits.push(tempVisit);
            }
        });
        logDataLoaded('location visits', visits.length);
        setVisits(visits);
    }, [location, isUserSocial]);

    useEffect(() => {
        if (!id) return;
        (async () => {
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                let locationToSet = docSnap.data() as Location;
                locationToSet.id = docSnap.id;
                setLocation(locationToSet);
            }
        })();
    }, [id]);

    useEffect(() => {
        if (!id || !props.currentUser?.id) return;
        
        (async () => {
            const visitQuery = query(
                collection(db, 'visits'),
                where('locationId', '==', id),
                where('userId', '==', props.currentUser!.id)
            );
            const visitSnap = await getDocs(visitQuery);
            if (!visitSnap.empty) {
                const visitData = visitSnap.docs[0].data() as Visit;
                setVisited(visitData.visited || false);
            }
        })();
    }, [id, props.currentUser?.id]);

    const toggleVisited = useCallback(() => {
        if (!id) return;
        
        const toggleVisitedFunction = httpsCallable(functions, 'toggleVisitedV2');
        toggleVisitedFunction({locationId: id}).then(async (response) => {
            const result = response.data as { status: number; code: string };
            if (result.status === 403) {
                alert(result.code);
                return;
            }
            
            const locationRef = doc(db, 'locations', id);
            const docSnap = await getDoc(locationRef);
            if (docSnap.exists()) {
                let locationToSet = docSnap.data() as Location;
                locationToSet.id = docSnap.id;
                setLocation(locationToSet);
            }
            
            setVisited(prev => !prev);
        }).catch((error) => {
            console.error('Error toggling visited:', error);
            alert('Failed to update visit status');
        });
    }, [id]);

    useEffect(() => {
        if (!location) return;
        loadVisits();
    }, [location, loadVisits]);

    const getUserId = useCallback((visitId: string): string => {
        if(!props.usersMap) {
            return "";
        }
        let user: User | undefined = props.usersMap.get(visitId);
        if(!user) {
            return "";
        }
        return user.id;
    }, [props.usersMap]);

    const getUser = useCallback((visitId: string): User => {
        if(!props.usersMap) {
            return {} as User;
        }
        let user: User | undefined = props.usersMap.get(visitId);
        if(!user) {
            return {} as User;
        }
        return user;
    }, [props.usersMap]);

    const isUiV2 = !!props.currentUser?.enableUiV2;
    const verifiedVisitCount = visits ? visits.filter((visit) => visit.verified).length : 0;

    if (isUiV2) {
        return (
            <>
                <PageTracking />
                {props.currentUser && <LocationDetailsV2
                    location={location}
                    currentUser={props.currentUser}
                    likesMap={props.likesMap}
                    visited={visited}
                    onToggleVisited={toggleVisited}
                    variant="header"
                />}
                {location && <V2GlassPanel className="location-v2-detail-toolbar">
                    <Link className="location-v2-back-button" to={`/location/${location.id}/comments`}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Pub
                    </Link>
                    <div className="location-v2-detail-stats">
                        <V2GlassSurface className="location-v2-detail-stat">
                            <span>Visits</span>
                            <b>{location.visitCount || 0}</b>
                        </V2GlassSurface>
                        <V2GlassSurface className="location-v2-detail-stat">
                            <span>Verified</span>
                            <b>{verifiedVisitCount}</b>
                        </V2GlassSurface>
                    </div>
                </V2GlassPanel>}
                <V2GlassPanel className="location-v2-detail-list-card v2-glass-list-panel">
                    {(props.usersMap && visits) && visits.map(visit => (
                        <V2GlassSurface key={getUserId(visit.userId)} className="location-v2-visit-row v2-glass-list-row">
                            <UserTag
                                visit={visit}
                                session={props.sessionsMap.get(visit.userId)}
                                user={getUser(visit.userId)}
                                padRight={true}
                                hideLocationDot={false}
                                linksToTab={"visits"}
                            />
                            <V2GlassChip className="location-v2-visit-date-chip">
                                <DateTime date={visit.lastModified}/>
                            </V2GlassChip>
                        </V2GlassSurface>
                    ))}
                    {visits && visits.length === 0 && <div className="location-v2-empty-list">No visits yet</div>}
                    {!visits && <div className="location-v2-empty-list">Loading...</div>}
                </V2GlassPanel>
            </>
        );
    }

    return (
        <>
            <PageTracking />
            {props.currentUser && (props.currentUser.enableUiV2 ? <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="header"
            /> : <LocationDetails
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
            />)}
            {location && props.currentUser && !props.currentUser.enableUiV2 && !props.currentUser.socialDisabled && <LocationCheckIn currentUser={props.currentUser} location={location}/>}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="location"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="tags"
            />}
            {props.currentUser?.enableUiV2 && <LocationDetailsV2
                location={location}
                currentUser={props.currentUser}
                likesMap={props.likesMap}
                visited={visited}
                onToggleVisited={toggleVisited}
                variant="widgets"
            />}
            {location && <LocationNav settings={props.settings} currentUser={props.currentUser} locationId={location.id}/>}
            <Card>
                {location && visits && visits.length > 0 && <Card.Header className={"header-padding"}>
                    <div className={"d-inline-block stats-card float-left mr-2"}>
                        <span>Total: <b>{location.visitCount || 0}</b></span>
                    </div>
                    <div className={"d-inline-block stats-card float-left"}>
                        <span>Verified: <b>{visits.filter((visit) => {return visit.verified}).length}</b></span>
                    </div>
                </Card.Header>}
                {(props.usersMap && visits) && visits.map(visit => (
                    <div key={getUserId(visit.userId)} className={"user-row"}>
                        <UserTag 
                            visit={visit} 
                            session={props.sessionsMap.get(visit.userId)} 
                            user={getUser(visit.userId)} 
                            padRight={true} 
                            hideLocationDot={false} 
                            linksToTab={"visits"} 
                        />
                        <DateTime date={visit.lastModified}/>
                    </div>
                ))}
            </Card>
        </>
    );
}

export default LocationVisits;
